import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { DASHBOARD_HRIS, DASHBOARD_OMS, photoFrame, photos } from '../../../content/media';

type Slide = {
  id: number;
  eyebrow: string;
  headline: string;
  subtext: string;
  cta1: string;
  cta2: string;
  image: string;
  image2?: string;
};

const slides: Slide[] = [
  {
    id: 4,
    eyebrow: 'Custom SaaS Platform',
    headline: 'Build.\nBrand.\nDeploy.',
    subtext: 'Enterprise-grade HRIS, OMS, and LMS modules — fully white-labelled and ready to launch under your brand. No dev team required.',
    cta1: 'Explore Platform',
    cta2: 'Book a Demo',
    image: DASHBOARD_HRIS,
    image2: DASHBOARD_OMS,
  },
  {
    id: 1,
    eyebrow: 'Managed IT Services',
    headline: 'Secure.\nScalable.\nReliable.',
    subtext: 'Enterprise-grade IT services that keep your business running at peak performance — every hour, every day.',
    cta1: 'Get Started',
    cta2: 'Book Consultation',
    image: photos.managedIt,
  },
  {
    id: 2,
    eyebrow: 'Cybersecurity',
    headline: 'Protected.\nDetected.\nResolved.',
    subtext: 'Advanced cybersecurity designed to protect your data, infrastructure, and reputation from every angle.',
    cta1: 'Explore Security',
    cta2: 'Request Assessment',
    image: photos.cybersecurity,
  },
  {
    id: 3,
    eyebrow: 'Cloud Computing',
    headline: 'Migrate.\nOptimize.\nGrow.',
    subtext: 'Seamless cloud migration and management that unlocks scalability, reduces costs, and powers transformation.',
    cta1: 'Explore Cloud',
    cta2: 'Talk to an Expert',
    image: photos.cloud,
  },
];

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback((index: number) => {
    if (animating) return;
    setAnimating(true);
    setCurrent(index);
    setTimeout(() => setAnimating(false), 700);
  }, [animating]);

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % slides.length);
    }, 7000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  const slide = slides[current];

  return (
    <section className="relative bg-white pt-28 lg:pt-32 pb-10 lg:pb-0 overflow-hidden">
      <div className="mx-auto px-6 lg:px-10 xl:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="relative z-10 max-w-xl">
            <div key={`eyebrow-${current}`} className="inline-flex items-center gap-2 mb-5 animate-fade-in">
              <span className="w-1.5 h-1.5 rounded-full bg-[#c97a2b] flex-shrink-0" />
              <span className="text-xs font-semibold text-[#c97a2b] uppercase tracking-[0.22em]">
                {slide.eyebrow}
              </span>
            </div>

            <h1
              key={`headline-${current}`}
              className="text-[40px] lg:text-[56px] xl:text-[64px] font-extrabold text-page-ink leading-[1.02] tracking-[-0.04em] mb-5 animate-fade-in-up whitespace-pre-line"
            >
              {slide.headline}
            </h1>

            <p
              key={`sub-${current}`}
              className="text-[15px] lg:text-base text-page-muted leading-relaxed mb-8 max-w-lg animate-fade-in-up font-light"
              style={{ animationDelay: '0.08s' }}
            >
              {slide.subtext}
            </p>

            <div
              key={`cta-${current}`}
              className="flex flex-wrap items-center gap-3 mb-8 animate-fade-in-up"
              style={{ animationDelay: '0.16s' }}
            >
              <Link to="/contact" className="btn-primary">
                {slide.cta1} <i className="ri-arrow-right-line text-sm" />
              </Link>
              <Link to="/contact" className="btn-ghost">
                {slide.cta2}
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => goTo((current - 1 + slides.length) % slides.length)}
                className="w-9 h-9 flex items-center justify-center rounded-full border border-black/10 text-page-ink hover:border-[#c97a2b] hover:text-[#c97a2b] transition-all cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              <div className="flex items-center gap-1.5">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goTo(i)}
                    className={`transition-all duration-400 rounded-full cursor-pointer ${
                      i === current ? 'w-7 h-1.5 bg-[#c97a2b]' : 'w-1.5 h-1.5 bg-black/15 hover:bg-black/35'
                    }`}
                  />
                ))}
              </div>
              <button
                onClick={() => goTo((current + 1) % slides.length)}
                className="w-9 h-9 flex items-center justify-center rounded-full border border-black/10 text-page-ink hover:border-[#c97a2b] hover:text-[#c97a2b] transition-all cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
              <span className="ml-2 text-page-faint text-xs font-mono tracking-widest">
                0{current + 1} / 0{slides.length}
              </span>
            </div>
          </div>

          <div className="relative lg:-mb-24 z-10">
            <div className={`${photoFrame} bg-white min-h-[320px] lg:min-h-[460px]`}>
              {slides.map((s, i) => (
                <div
                  key={s.id}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    i === current ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                >
                  {s.image2 ? (
                    <div className="absolute inset-0 flex flex-col gap-2 p-3 bg-white">
                      <div className="flex-1 relative rounded-2xl overflow-hidden">
                        <img src={s.image} alt="HRIS Dashboard" className="absolute inset-0 w-full h-full object-cover object-top" />
                      </div>
                      <div className="flex-1 relative rounded-2xl overflow-hidden">
                        <img src={s.image2} alt="OMS Dashboard" className="absolute inset-0 w-full h-full object-cover object-top" />
                      </div>
                    </div>
                  ) : (
                    <img
                      src={s.image}
                      alt={s.eyebrow}
                      className="absolute inset-0 w-full h-full object-cover object-center"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
